/**
 * PackageSizeGenerator - Generates package dimensions
 * 
 * Separates size calculation from physics body creation.
 * Handles:
 * - Size class selection (Small 35%, Medium 50%, Large 15%)
 * - Minimum grab size constraint (44px for mobile)
 * - Maximum size constraint (40% of container width)
 * - Logarithmic aspect ratio distribution
 * - Size-based mass calculation
 */

import {
    SIZE_CONSTRAINTS,
    BOX_SIZE_CLASSES,
    BOX_ASPECT_RATIO,
    BAG_SIZE,
    TRIANGLE_SIZE,
    MASS_SCALING,
    REFERENCE_WIDTH,
} from './Constants';

// ============================================================================
// TYPES
// ============================================================================

/** Generated box dimensions */
export interface BoxDimensions {
    width: number;
    height: number;
    density: number;
    area: number;
    sizeClass: 'small' | 'medium' | 'large';
}

/** Generated bag dimensions */
export interface BagDimensions {
    width: number;
    height: number;
    chamferRadius: number;
    density: number;
    area: number;
}

/** Generated triangle dimensions */
export interface TriangleDimensions {
    radius: number;
    density: number;
    area: number;
    /** Initial rotation (0, 90, 180, or 270 degrees in radians) */
    rotation: number;
}

// ============================================================================
// PACKAGE SIZE GENERATOR
// ============================================================================

/**
 * PackageSizeGenerator - Generates package dimensions with proper constraints
 */
export class PackageSizeGenerator {
    private scaleFactor: number = 1;
    private maxDimension: number = Infinity;

    /**
     * Set the scale factor based on container width.
     * Also calculates the maximum dimension constraint.
     */
    setScaleFactor(containerWidth: number): void {
        this.scaleFactor = containerWidth / REFERENCE_WIDTH;
        this.maxDimension = containerWidth * SIZE_CONSTRAINTS.maxSizePercent;
    }

    /**
     * Get the current scale factor.
     */
    getScaleFactor(): number {
        return this.scaleFactor;
    }

    /**
     * Generate box dimensions using size class system.
     * 
     * Algorithm:
     * 1. Select size class based on weights (Small 35%, Medium 50%, Large 15%)
     * 2. Generate random area within class bounds
     * 3. Generate aspect ratio using logarithmic distribution
     * 4. Calculate dimensions from area and aspect ratio
     * 5. Apply min grab size and max dimension constraints
     * 6. Calculate density based on area (larger = heavier)
     */
    generateBox(): BoxDimensions {
        // Select size class based on weights
        const sizeClass = this.selectSizeClass();
        const classConfig = BOX_SIZE_CLASSES[sizeClass];

        // Scale area by scaleFactor squared
        const areaScale = this.scaleFactor * this.scaleFactor;
        const minArea = classConfig.minArea * areaScale;
        const maxArea = classConfig.maxArea * areaScale;

        // Random area within class bounds (linear distribution within class)
        const area = randomRange(minArea, maxArea);

        // Logarithmic distribution for aspect ratio
        const aspectRatio = this.generateAspectRatio();

        // Calculate dimensions from area and aspect ratio
        // area = width * height, aspectRatio = width / height
        // height = sqrt(area / aspectRatio), width = height * aspectRatio
        let height = Math.sqrt(area / aspectRatio);
        let width = height * aspectRatio;

        // Apply constraints
        const minSize = SIZE_CONSTRAINTS.minGrabSize * this.scaleFactor;
        
        // Ensure minimum grab size
        if (width < minSize) {
            width = minSize;
            height = area / width;
        }
        if (height < minSize) {
            height = minSize;
            width = area / height;
        }

        // Ensure maximum dimension
        if (width > this.maxDimension) {
            const ratio = this.maxDimension / width;
            width = this.maxDimension;
            height = height * ratio;
        }
        if (height > this.maxDimension) {
            const ratio = this.maxDimension / height;
            height = this.maxDimension;
            width = width * ratio;
        }

        // Final clamp to ensure constraints are met
        width = clamp(width, minSize, this.maxDimension);
        height = clamp(height, minSize, this.maxDimension);

        // Calculate actual area after constraint adjustments
        const actualArea = width * height;

        // Calculate density based on area (larger = heavier)
        const density = this.calculateDensity(actualArea);

        return {
            width,
            height,
            density,
            area: actualArea,
            sizeClass,
        };
    }

    /**
     * Generate bag dimensions.
     * Bags use simple linear random ranges.
     */
    generateBag(): BagDimensions {
        const minSize = SIZE_CONSTRAINTS.minGrabSize * this.scaleFactor;

        // Scale dimensions
        let width = randomRange(
            BAG_SIZE.width.min * this.scaleFactor,
            BAG_SIZE.width.max * this.scaleFactor
        );
        let height = randomRange(
            BAG_SIZE.height.min * this.scaleFactor,
            BAG_SIZE.height.max * this.scaleFactor
        );

        // Ensure minimum grab size
        width = Math.max(width, minSize);
        height = Math.max(height, minSize);

        // Ensure maximum dimension
        width = Math.min(width, this.maxDimension);
        height = Math.min(height, this.maxDimension);

        // Chamfer scales with smaller dimension
        const smallerDim = Math.min(width, height);
        const chamferRadius = smallerDim * BAG_SIZE.chamferRatio;

        const area = width * height;
        const density = this.calculateDensity(area) * 0.8; // Bags are slightly lighter

        return {
            width,
            height,
            chamferRadius,
            density,
            area,
        };
    }

    /**
     * Generate triangle dimensions.
     */
    generateTriangle(): TriangleDimensions {
        const minSize = SIZE_CONSTRAINTS.minGrabSize * this.scaleFactor;

        // Scale radius
        let radius = randomRange(
            TRIANGLE_SIZE.radius.min * this.scaleFactor,
            TRIANGLE_SIZE.radius.max * this.scaleFactor
        );

        // Ensure minimum grab size (radius should be at least half the grab size)
        radius = Math.max(radius, minSize / 2);

        // Ensure maximum dimension (diameter should fit)
        radius = Math.min(radius, this.maxDimension / 2);

        // Random 90° rotation
        const rotation = Math.floor(Math.random() * 4) * (Math.PI / 2);

        // Approximate area of equilateral triangle: (sqrt(3)/4) * side²
        // For our triangle with radius r, side ≈ r * sqrt(3)
        const area = (Math.sqrt(3) / 4) * Math.pow(radius * Math.sqrt(3), 2);
        const density = this.calculateDensity(area) * 0.8; // Triangles are slightly lighter

        return {
            radius,
            density,
            area,
            rotation,
        };
    }

    /**
     * Select a size class based on configured weights.
     * Small: 35%, Medium: 50%, Large: 15%
     */
    private selectSizeClass(): 'small' | 'medium' | 'large' {
        const roll = Math.random();
        const smallThreshold = BOX_SIZE_CLASSES.small.weight;
        const mediumThreshold = smallThreshold + BOX_SIZE_CLASSES.medium.weight;

        if (roll < smallThreshold) {
            return 'small';
        } else if (roll < mediumThreshold) {
            return 'medium';
        } else {
            return 'large';
        }
    }

    /**
     * Generate a logarithmic aspect ratio.
     * Equal probability for 1:4 and 4:1 ratios.
     */
    private generateAspectRatio(): number {
        const logMin = Math.log(BOX_ASPECT_RATIO.min);
        const logMax = Math.log(BOX_ASPECT_RATIO.max);
        return Math.exp(logMin + Math.random() * (logMax - logMin));
    }

    /**
     * Calculate density based on area.
     * Larger packages are heavier.
     */
    private calculateDensity(area: number): number {
        const scaledRefArea = MASS_SCALING.referenceArea * this.scaleFactor * this.scaleFactor;
        
        // Calculate ratio: areas larger than reference get higher density
        const areaRatio = area / scaledRefArea;
        
        // Use sqrt to moderate the effect (doubling area doesn't double mass)
        const densityMultiplier = Math.sqrt(areaRatio);
        
        // Clamp to configured range
        return clamp(
            densityMultiplier,
            MASS_SCALING.minDensity,
            MASS_SCALING.maxDensity
        );
    }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/** Clamp a value between min and max */
function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

/** Get a random value in a range */
function randomRange(min: number, max: number): number {
    return min + Math.random() * (max - min);
}
